import {Component, EventEmitter, Output, effect, input, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldOptionsModule} from 'se-fe-form-field-options';
import {SeFeSelectModule, SeFeSelectOptions} from 'se-fe-select';
import {ProductQueryParams} from '../../../models';

const STATUS_FILTER_PARAMS: Record<string, Pick<ProductQueryParams, 'status' | 'active'>> = {
  active: {status: 'active', active: true},
  inactive: {status: 'inactive', active: false},
};
const EMPTY_STATUS_FILTER_PARAMS: Pick<ProductQueryParams, 'status' | 'active'> = {
  status: undefined,
  active: undefined,
};
const ACTIVE_STATUS_FILTER_VALUES = new Map<boolean, string>([
  [true, 'active'],
  [false, 'inactive'],
]);

@Component({
  selector: 'product-table-filters',
  templateUrl: './product-table-filters.component.html',
  imports: [ReactiveFormsModule, SeFeButtonModule, SeFeFormFieldModule, SeFeFormFieldOptionsModule, SeFeSelectModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-table-filters.component.scss',
})
export class ProductTableFiltersComponent {
  @Output() filtersApplied = new EventEmitter<ProductQueryParams>();

  title = input.required<string>();
  active = input<boolean | null | undefined>();
  catalogId = input<number | null | undefined>();
  showCatalogFilter = input(false);
  catalogFilterOptions = input<SeFeSelectOptions>({sections: []});

  readonly statusFilterControl = new FormControl('', {nonNullable: true});
  readonly catalogFilterControl = new FormControl('', {nonNullable: true});
  readonly filterForm = new FormGroup({
    status: this.statusFilterControl,
    catalog: this.catalogFilterControl,
  });
  readonly statusFilterOptions = [
    {label: 'Active', value: 'active'},
    {label: 'Inactive', value: 'inactive'},
  ];
  readonly catalogFilterConfig: SeFeFormFieldConfig = {
    label: 'Catalog',
  };

  constructor() {
    effect(() => {
      this.syncStatusFilterControl(this.active());
    });

    effect(() => {
      this.syncCatalogFilterControl(this.catalogId());
    });
  }

  applyFilters(): void {
    this.filtersApplied.emit(this.selectedFilterParams());
  }

  clearFilters(): void {
    this.statusFilterControl.setValue('');
    this.catalogFilterControl.setValue('');
    this.filtersApplied.emit(this.selectedFilterParams());
  }

  private syncStatusFilterControl(active: boolean | null | undefined): void {
    const nextValue = active === undefined || active === null ? '' : (ACTIVE_STATUS_FILTER_VALUES.get(active) ?? '');
    this.statusFilterControl.setValue(nextValue, {emitEvent: false});
  }

  private syncCatalogFilterControl(catalogId: number | null | undefined): void {
    this.catalogFilterControl.setValue(catalogId ? String(catalogId) : '', {emitEvent: false});
  }

  private selectedFilterParams(): ProductQueryParams {
    return {
      page: 1,
      ...this.selectedStatusParams(),
      ...this.selectedCatalogParams(),
    };
  }

  private selectedStatusParams(): Pick<ProductQueryParams, 'status' | 'active'> {
    return STATUS_FILTER_PARAMS[this.statusFilterControl.value] ?? EMPTY_STATUS_FILTER_PARAMS;
  }

  private selectedCatalogParams(): Pick<ProductQueryParams, 'catalogId'> {
    const catalogId = Number(this.catalogFilterControl.value);

    return Number.isFinite(catalogId) && catalogId > 0 ? {catalogId} : {catalogId: undefined};
  }
}
